import { useState } from "react";
import { AdjustCreditForm } from "./AdjustCreditForm";
import { Modal } from "./Modal";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  LoadingBlock,
  Table,
  Td,
  Th,
  cx,
} from "./ui";
import { ApiError } from "../lib/api";
import { formatRelative } from "../lib/format";
// Only the divisor. This file's `money` below words itself for this card and is
// deliberately its own; the billing constant is not the kind of thing a
// component gets to have a local copy of, which is what a hardcoded 730 here
// was until the divisor moved.
import { HOURS_PER_MONTH } from "../lib/money";
import {
  useAdjustCredit,
  useOrgBilling,
  useRecomputeBalance,
  type AdjustCreditInput,
} from "../lib/queries";

const MICROS_PER_UNIT = 1_000_000;

/**
 * Ledger entry kinds, named for a reader.
 *
 * This was `capitalize` on the raw value, which was fine while every type was a
 * word — and stopped being fine when `expiry` arrived, because "Expiry" does not
 * say that promotional credit was taken back. The one an operator most needs to
 * recognise was the one the CSS could not name.
 */
const ENTRY_LABELS: Record<string, string> = {
  topup: "Top-up",
  trial: "Trial credit",
  promo: "Promotion",
  usage: "Usage",
  adjustment: "Adjustment",
  refund: "Refund",
  expiry: "Promotion expired",
  reversal: "Reversal — supplier refused",
};

function money(micros: number, currency = "USD"): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(
      micros / MICROS_PER_UNIT,
    );
  } catch {
    return `${(micros / MICROS_PER_UNIT).toFixed(2)} ${currency}`;
  }
}

/**
 * A tenant's credit, and the one lever an operator has over it.
 *
 * Adjustments always carry a reason and are always attributed — an unexplained
 * credit is indistinguishable from theft when someone reads the audit log back.
 * They are also confirmed with the operator's password and a current code,
 * bounded per adjustment and per operator, refused on an organization the
 * operator belongs to, and mailed to every other operator the moment they
 * apply. All of that is the API's; this card's job is to say so before the
 * operator finds out from a refusal, which is why the dialog opens on the
 * policy rather than on the amount.
 */
export function OrgCreditCard({ orgId, canWrite }: { orgId: string; canWrite: boolean }) {
  const billing = useOrgBilling(orgId);
  const adjust = useAdjustCredit(orgId);
  const recompute = useRecomputeBalance(orgId);

  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function submit(input: AdjustCreditInput) {
    setError(null);
    try {
      const result = await adjust.mutateAsync(input);
      setOpen(false);
      setNotice(
        [
          "Credit applied.",
          result.restored ? "The organization's suspension has been lifted." : null,
          result.notifiedOperators
            ? "Every operator has been emailed about it."
            : "No operator email went out — mail is not configured here.",
        ]
          .filter(Boolean)
          .join(" "),
      );
    } catch (cause) {
      // Refusals arrive as the API's own sentence — a wrong code, a member
      // adjusting their own organization, a window used up — and are shown as
      // sent, because each says what to do next.
      setError(cause instanceof ApiError ? cause.message : "Could not adjust the balance.");
    }
  }

  async function fix() {
    setNotice(null);
    setError(null);
    try {
      const result = await recompute.mutateAsync();
      setNotice(
        result.driftMicros === 0
          ? "Balance matches the ledger."
          : `Balance corrected by ${money(result.driftMicros)}.`,
      );
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not recompute.");
    }
  }

  const data = billing.data;

  return (
    <Card>
      <CardHeader
        title="Credit"
        description="Prepaid balance, consumed hourly by running apps."
        action={
          canWrite ? (
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" loading={recompute.isPending} onClick={() => void fix()}>
                Recompute
              </Button>
              <Button
                size="sm"
                disabled={!data || data.adjustmentPolicy.youAreMember}
                title={
                  data?.adjustmentPolicy.youAreMember
                    ? "You're a member of this organization, so another operator has to adjust its credit."
                    : undefined
                }
                onClick={() => setOpen(true)}
              >
                Adjust
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className="space-y-4 p-5">
        {notice && <Alert tone="success">{notice}</Alert>}
        {error && !open && <Alert>{error}</Alert>}
        {/*
          Said on the card rather than only on a disabled button's tooltip,
          because a control that will not press and does not say why reads as
          broken. The API refuses this too; the note is so nobody has to try.
        */}
        {canWrite && data?.adjustmentPolicy.youAreMember && (
          <p className="text-xs text-fg-subtle">
            You're a member of this organization, so its credit has to be adjusted by another
            operator. Adjust stays off for you here.
          </p>
        )}

        {billing.isLoading ? (
          <LoadingBlock />
        ) : !data ? (
          <p className="text-sm text-fg-subtle">Could not load billing.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
              <div>
                <p className="text-xs text-fg-subtle">Balance</p>
                <p
                  className={cx(
                    "text-xl font-semibold",
                    data.balanceMicros <= 0
                      ? "text-danger"
                      : "text-fg",
                  )}
                >
                  {money(data.balanceMicros)}
                </p>
              </div>
              {/*
                The rate, without the projection that used to sit beside it. A
                runway divided a balance by a near-zero burn and printed the
                result; the monthly figure is the fact, and who is actually about
                to run out is the at-risk list on the revenue page.
              */}
              <div>
                <p className="text-xs text-fg-subtle">Burn</p>
                <p className="text-sm text-fg tabular-nums">
                  {money(data.burnPerHourMicros * HOURS_PER_MONTH)}/mo
                </p>
              </div>
              {data.trialMicros !== null && (
                <div>
                  <p className="text-xs text-fg-subtle">Granted</p>
                  <p className="text-sm text-fg">{money(data.trialMicros)} trial</p>
                </div>
              )}
              {/*
                What the balance is actually made of, when part of it is
                promotional. This is the answer to the support ticket the card
                exists for: a $300 balance that cannot buy a single token is not a
                fault, and an operator reading only the balance has no way to know
                that.
              */}
              {data.promo.liveMicros + data.promo.lapsedMicros > 0 && (
                <div>
                  <p className="text-xs text-fg-subtle">Promotional</p>
                  <p className="text-sm text-fg">
                    {money(data.promo.liveMicros)}
                    <span className="text-fg-subtle"> · {money(data.promo.spendableOnAiMicros)} for AI</span>
                  </p>
                  {data.promo.lapsedMicros > 0 && (
                    <p className="text-xs text-warn">
                      {money(data.promo.lapsedMicros)} lapsed, awaiting sweep
                    </p>
                  )}
                </div>
              )}
              {data.suspendedForCredit && (
                <span className="rounded bg-danger/10 px-2 py-0.5 text-xs font-medium text-danger">
                  Suspended for non-payment
                </span>
              )}
              {/*
                The state that explains a 402 over a healthy balance.

                Shown only while it is shut, because "AI open" on every other
                tenant is a badge that means nothing — this is here to answer one
                support ticket, not to label the normal case. Adjusting the
                balance does not clear it, which is worth saying on the card
                where the Adjust button is.

                Named for the state rather than for its cause. It read "no
                top-up yet", which was the only way in until a campaign could pay
                out AI-spendable credit — so the badge asserted a reason that is
                now sometimes wrong about an account it is right about.
              */}
              {!data.inferenceUnlocked && (
                <span
                  className="rounded bg-warn/10 px-2 py-0.5 text-xs font-medium text-warn"
                  title="Opens on a payment, on credit you adjust in below, or on credit from a campaign marked AI-spendable. An ordinary promotion does not open it."
                >
                  AI locked
                </span>
              )}
            </div>

            {data.entries.length > 0 && (
              <Table
                head={
                  <tr>
                    <Th priority="sm">When</Th>
                    <Th priority="md">Type</Th>
                    <Th>Description</Th>
                    <Th numeric>Amount</Th>
                  </tr>
                }
              >
                {data.entries.slice(0, 10).map((entry) => (
                  <tr key={entry.id}>
                    <Td priority="sm">{formatRelative(entry.createdAt)}</Td>
                    <Td priority="md">{ENTRY_LABELS[entry.type] ?? entry.type}</Td>
                    <Td className="text-fg-subtle">{entry.description ?? "—"}</Td>
                    <Td numeric>
                      {entry.amountMicros > 0 ? "+" : "−"}
                      {money(Math.abs(entry.amountMicros))}
                    </Td>
                  </tr>
                ))}
              </Table>
            )}
          </>
        )}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        dismissable={!adjust.isPending}
        title="Adjust credit"
        description="Positive adds, negative removes. Confirmed with your password and a current code, logged against you, and emailed to every operator."
      >
        {data && (
          <AdjustCreditForm
            policy={data.adjustmentPolicy}
            pending={adjust.isPending}
            error={error}
            onSubmit={(input) => void submit(input)}
            onCancel={() => setOpen(false)}
          />
        )}
      </Modal>
    </Card>
  );
}
